import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMap } from '../../context/MapContext';
import MapPopup from './MapPopup';
import MapToolbar from './MapToolbar';
import MapZoomControls from './MapZoomControls';
import MapLegend from './MapLegend';
import MapHeader from './MapHeader';
import MunicipalitySelector from '../MunicipalitySelector/MunicipalitySelector';
import { cartoTileUrl } from '../../config/carto';
import './MapView.css';

// Fix default marker icons for Leaflet + webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function createBaseLayers() {
  return {
    'CartoDB Dark': L.tileLayer(
      cartoTileUrl('dark_all'),
      { attribution: '&copy; CartoDB', maxZoom: 20 }
    ),
    'CartoDB Light': L.tileLayer(
      cartoTileUrl('rastertiles/voyager'),
      { attribution: '&copy; CartoDB', maxZoom: 20 }
    ),
    OpenStreetMap: L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      { attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 }
    ),
    'Esri Satellite': L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution: '&copy; Esri', maxZoom: 19 }
    ),
    'Esri Topo': L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
      { attribution: '&copy; Esri', maxZoom: 19 }
    ),
    OpenTopoMap: L.tileLayer(
      'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
      { attribution: '&copy; OpenTopoMap contributors', subdomains: 'abc', maxZoom: 17 }
    ),
    'Stadia Dark': L.tileLayer(
      'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png',
      { attribution: '&copy; Stadia Maps', maxZoom: 20 }
    ),
    IBGE: L.tileLayer.wms('https://geoservicos.ibge.gov.br/geoserver/wms', {
      layers: 'IBGE:base_geografica_continua',
      format: 'image/png',
      transparent: true,
      attribution: '&copy; IBGE',
    }),
    'Sem camada': L.tileLayer(
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
    ),
  };
}

function getMunicipalityOutlineStyle(theme) {
  const isDark = theme === 'dark';

  return {
    pane: 'municipality-outline-pane',
    color: isDark ? '#f8fafc' : '#002855',
    weight: isDark ? 3 : 2.5,
    opacity: isDark ? 0.98 : 0.92,
    dashArray: '8 6',
    lineCap: 'round',
    lineJoin: 'round',
    fill: false,
    interactive: false,
  };
}

function MapView() {
  const {
    wmsLayers,
    theme,
    zoomRequest,
    clearZoomRequest,
    removeAllWmsLayers,
    selectedMunicipality,
  } = useMap();

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const baseLayersRef = useRef(null);
  const activeBaseLayerRef = useRef(null);
  const layerControlRef = useRef(null);
  const wmsLayerRefsRef = useRef(new Map()); // id -> L.TileLayer.WMS
  const municipalityOutlineRef = useRef(null);
  const municipalityOutlineCodeRef = useRef(null);
  const municipalityOutlineFetchRef = useRef(0);
  const popupContainerRef = useRef(null);

  const abortRef = useRef(null);
  const gfiMsgTimerRef = useRef(null);

  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [popup, setPopup] = useState(null);       // { feature, pixel, layerName }
  const [gfiLoading, setGfiLoading] = useState(false);
  const [gfiMessage, setGfiMessage] = useState(null); // null | 'empty' | 'timeout'

  // Initialize map once
  useEffect(() => {
    if (mapRef.current) return;

    const baseLayers = createBaseLayers();
    baseLayersRef.current = baseLayers;

    const initialBase = baseLayers['CartoDB Light'];
    activeBaseLayerRef.current = initialBase;

    const map = L.map(mapContainerRef.current, {
      center: [-15.7801, -47.9292],
      zoom: 4,
      layers: [initialBase],
      zoomControl: false,   // replaced by custom toolbar
      keepBuffer: 4,        // pre-carrega mais tiles ao redor da viewport
      zoomAnimation: true,
      fadeAnimation: true,
    });

    mapRef.current = map;

    map.createPane('municipality-outline-pane');
    map.getPane('municipality-outline-pane').style.zIndex = 460;
    map.getPane('municipality-outline-pane').style.pointerEvents = 'none';

    // Scale control
    L.control.scale({ position: 'bottomleft', imperial: false }).addTo(map);

    // Coordinate display on mouse move
    map.on('mousemove', (e) => {
      setCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    map.on('mouseout', () => {
      setCoords({ lat: null, lng: null });
    });

    // GetFeatureInfo on click — queries all visible layers in parallel
    map.on('click', async (e) => {
      const mapStore = window.__ideWmsLayersRef;
      if (!mapStore) return;

      const visibleLayers = [...mapStore]
        .filter((l) => l.visible)
        .sort((a, b) => a.order - b.order);

      if (visibleLayers.length === 0) return;

      // Cancel any in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Clear any pending "no results" toast
      if (gfiMsgTimerRef.current) clearTimeout(gfiMsgTimerRef.current);
      setGfiMessage(null);

      const pixelPoint = map.latLngToContainerPoint(e.latlng);
      const size = map.getSize();
      const bounds = map.getBounds();
      const bbox =
        bounds.getWest() + ',' + bounds.getSouth() + ',' +
        bounds.getEast() + ',' + bounds.getNorth();

      setGfiLoading(true);
      setPopup(null);

      // 10-second hard timeout
      const timeoutId = setTimeout(() => controller.abort('timeout'), 10000);

      const requests = visibleLayers.map((tl) => {
        // WMS 1.3.0 uses CRS= instead of SRS=; CRS:84 keeps lon/lat axis order
        const srsParam = tl.version === '1.3.0' ? 'crs=CRS:84' : 'srs=EPSG:4326';
        const gfiUrl =
          `${tl.baseUrl}?service=WMS` +
          `&version=${tl.version}` +
          `&request=GetFeatureInfo` +
          `&layers=${tl.layers}` +
          `&query_layers=${tl.layers}` +
          `&info_format=application/json` +
          `&feature_count=5` +
          `&x=${Math.round(pixelPoint.x)}` +
          `&y=${Math.round(pixelPoint.y)}` +
          `&width=${size.x}` +
          `&height=${size.y}` +
          `&${srsParam}` +
          `&bbox=${bbox}`;

        return fetch(`/api/wms-proxy?url=${encodeURIComponent(gfiUrl)}`, { signal: controller.signal })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            const features = data?.features || [];
            if (features.length === 0) return null;
            return { feature: features[0], layerName: tl.name || tl.layers };
          })
          .catch((err) => {
            if (err.name === 'AbortError') return null;
            console.warn('GetFeatureInfo layer error:', err);
            return null;
          });
      });

      try {
        const results = (await Promise.all(requests)).filter(Boolean);
        clearTimeout(timeoutId);

        if (results.length === 0) {
          // Show toast unless this request was superseded by a newer click
          if (abortRef.current === controller) {
            const reason = controller.signal.reason === 'timeout' ? 'timeout' : 'empty';
            setGfiMessage(reason);
            gfiMsgTimerRef.current = setTimeout(() => setGfiMessage(null), 3500);
          }
          return;
        }
        setPopup({ results, pixel: [pixelPoint.x, pixelPoint.y] });
      } catch (err) {
        clearTimeout(timeoutId);
        if (err.name !== 'AbortError') console.warn('GetFeatureInfo failed:', err);
      } finally {
        // Only clear loading if this is still the active request
        if (abortRef.current === controller) setGfiLoading(false);
      }
    });

    // Create popup container div inside map container
    const popupDiv = document.createElement('div');
    popupDiv.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:500;';
    mapContainerRef.current.appendChild(popupDiv);
    popupContainerRef.current = popupDiv;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store wmsLayers in a window ref so the click handler can access current value
  useEffect(() => {
    window.__ideWmsLayersRef = wmsLayers;
  }, [wmsLayers]);

  // Sync WMS layers with map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const refs = wmsLayerRefsRef.current;
    const currentIds = new Set(wmsLayers.map((l) => l.id));

    // Remove layers that are no longer in wmsLayers
    for (const [id, tileLayer] of refs.entries()) {
      if (!currentIds.has(id)) {
        map.removeLayer(tileLayer);
        refs.delete(id);
      }
    }

    // Add or update layers
    for (const wl of wmsLayers) {
      if (!refs.has(wl.id)) {
        // Create new WMS tile layer
        const wmsParams = {
          layers: wl.layers,
          format: wl.format,
          transparent: wl.transparent,
          version: wl.version,
          attribution: wl.name,
        };
        if (wl.cqlFilter) wmsParams.CQL_FILTER = wl.cqlFilter;
        const tileLayer = L.tileLayer.wms(wl.baseUrl, wmsParams);
        tileLayer.options._visible = wl.visible;
        if (wl.visible) {
          tileLayer.setOpacity(wl.opacity);
          tileLayer.addTo(map);
        } else {
          tileLayer.setOpacity(0);
          tileLayer.addTo(map);
        }
        refs.set(wl.id, tileLayer);
      } else {
        // Update existing layer
        const tileLayer = refs.get(wl.id);
        tileLayer.options._visible = wl.visible;
        tileLayer.setOpacity(wl.visible ? wl.opacity : 0);

        // zIndex atualizado no bloco final abaixo
      }
    }

    // Set zIndex for all — order=0 (primeiro na lista) fica no topo
    const maxOrder = wmsLayers.length - 1;
    for (const wl of wmsLayers) {
      const tileLayer = refs.get(wl.id);
      if (tileLayer && tileLayer.setZIndex) {
        tileLayer.setZIndex(200 + (maxOrder - wl.order));
      }
    }
  }, [wmsLayers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const clearMunicipalityOutline = () => {
      if (municipalityOutlineRef.current) {
        map.removeLayer(municipalityOutlineRef.current);
        municipalityOutlineRef.current = null;
      }
      municipalityOutlineCodeRef.current = null;
    };

    if (!selectedMunicipality?.code) {
      clearMunicipalityOutline();
      return undefined;
    }

    const currentCode = selectedMunicipality.code;

    if (
      municipalityOutlineRef.current &&
      municipalityOutlineCodeRef.current === currentCode
    ) {
      municipalityOutlineRef.current.setStyle(getMunicipalityOutlineStyle(theme));
      return undefined;
    }

    const fetchId = ++municipalityOutlineFetchRef.current;
    clearMunicipalityOutline();

    const geoJsonUrl =
      `https://servicodados.ibge.gov.br/api/v3/malhas/municipios/${currentCode}` +
      '?formato=application/vnd.geo+json';

    fetch(`/api/wms-proxy?url=${encodeURIComponent(geoJsonUrl)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || municipalityOutlineFetchRef.current !== fetchId) return;

        const outlineLayer = L.geoJSON(data, {
          style: getMunicipalityOutlineStyle(theme),
          interactive: false,
          pane: 'municipality-outline-pane',
        });

        outlineLayer.addTo(map);
        municipalityOutlineRef.current = outlineLayer;
        municipalityOutlineCodeRef.current = currentCode;

        const bounds = outlineLayer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, {
            padding: [36, 36],
            maxZoom: 12,
          });
        }
      })
      .catch((err) => {
        if (municipalityOutlineFetchRef.current === fetchId) {
          console.warn('MapView: municipality outline geojson failed', err);
        }
      });

    return undefined;
  }, [selectedMunicipality, theme]);

  // Handle zoomRequest
  useEffect(() => {
    if (!zoomRequest || !mapRef.current) return;
    const [minLat, minLng, maxLat, maxLng] = zoomRequest;
    if (
      isFinite(minLat) &&
      isFinite(minLng) &&
      isFinite(maxLat) &&
      isFinite(maxLng)
    ) {
      mapRef.current.fitBounds([
        [minLat, minLng],
        [maxLat, maxLng],
      ]);
    }
    clearZoomRequest();
  }, [zoomRequest, clearZoomRequest]);

  // Switch base layer when theme changes
  const prevThemeRef = useRef(theme);
  useEffect(() => {
    const map = mapRef.current;
    const baseLayers = baseLayersRef.current;
    if (!map || !baseLayers) return;
    if (prevThemeRef.current === theme) return;
    prevThemeRef.current = theme;

    const current = activeBaseLayerRef.current;
    let newBase = null;

    if (current === baseLayers['CartoDB Dark'] || !map.hasLayer(current)) {
      newBase = theme === 'dark' ? baseLayers['CartoDB Dark'] : baseLayers['CartoDB Light'];
    } else if (current === baseLayers['CartoDB Light']) {
      newBase = theme === 'dark' ? baseLayers['CartoDB Dark'] : baseLayers['CartoDB Light'];
    }

    if (newBase && newBase !== current) {
      // Only switch if current base is a CartoDB layer
      const isCartoDark = map.hasLayer(baseLayers['CartoDB Dark']);
      const isCartoLight = map.hasLayer(baseLayers['CartoDB Light']);

      if (isCartoDark && theme === 'light') {
        map.removeLayer(baseLayers['CartoDB Dark']);
        baseLayers['CartoDB Light'].addTo(map);
        activeBaseLayerRef.current = baseLayers['CartoDB Light'];
      } else if (isCartoLight && theme === 'dark') {
        map.removeLayer(baseLayers['CartoDB Light']);
        baseLayers['CartoDB Dark'].addTo(map);
        activeBaseLayerRef.current = baseLayers['CartoDB Dark'];
      }
    }
  }, [theme]);

  const handleClearLayers = useCallback(() => {
    if (window.confirm('Remover todas as camadas WMS do mapa?')) {
      removeAllWmsLayers();
    }
  }, [removeAllWmsLayers]);

  const handleClosePopup = useCallback(() => {
    setPopup(null);
  }, []);

  const visibleLayers = wmsLayers.filter((l) => l.visible);
  const headerClass = visibleLayers.length > 1
    ? 'has-header-tall'
    : visibleLayers.length === 1
      ? 'has-header'
      : '';

  return (
    <div className={`mapview-wrapper${headerClass ? ` ${headerClass}` : ''}`}>
      <div ref={mapContainerRef} className="mapview-container" />

      {/* Seletor de município — barra full-width no topo do mapa */}
      <div className="mapview-mun-bar">
        <MunicipalitySelector />
      </div>

      {/* Header bar — appears when layers are active */}
      <MapHeader />

      {/* Toolbar direita — camadas, toponímia, geolocalização */}
      <MapToolbar
        mapRef={mapRef}
        baseLayersRef={baseLayersRef}
        activeBaseLayerRef={activeBaseLayerRef}
      />

      {/* Toolbar esquerda inferior — Brasil, zoom +/- */}
      <MapZoomControls mapRef={mapRef} />

      {/* Coordinates overlay — só aparece quando o cursor está sobre o mapa */}
      {coords.lat !== null && (
        <div className="mapview-coords">
          Lat: {coords.lat.toFixed(4)} | Lng: {coords.lng.toFixed(4)}
        </div>
      )}

      {/* GFI loading indicator */}
      {gfiLoading && (
        <div className="mapview-gfi-loading" role="status" aria-live="polite">
          <span className="mapview-gfi-spinner" aria-hidden="true" />
          <span>Consultando feições...</span>
        </div>
      )}

      {/* GFI result toast — empty or timeout */}
      {!gfiLoading && gfiMessage && (
        <div className={`mapview-gfi-toast mapview-gfi-toast--${gfiMessage}`} role="status" aria-live="polite">
          {gfiMessage === 'timeout'
            ? 'Tempo de consulta esgotado'
            : 'Nenhuma feição encontrada neste ponto'}
        </div>
      )}

      {/* Popup portal */}
      {popup && popupContainerRef.current
        ? ReactDOM.createPortal(
            <MapPopup
              results={popup.results}
              pixel={popup.pixel}
              containerSize={
                mapContainerRef.current
                  ? { width: mapContainerRef.current.offsetWidth, height: mapContainerRef.current.offsetHeight }
                  : undefined
              }
              onClose={handleClosePopup}
            />,
            popupContainerRef.current
          )
        : null}

      {/* Legend — bottom-right corner */}
      <MapLegend />
    </div>
  );
}

export default MapView;
